import { useEffect, useMemo, useState } from "react";
import { fetchHospitals } from "../services/hospitals";

/**
 * Single source of truth for the color assigned to each hospital across the whole
 * dashboard — the Scan Map legend/markers, the Live Activity "Hospital Performance
 * Comparison" chart, the Calendar, etc. Every view builds its colors from the SAME
 * canonical ordering (alphabetical over the full hospital list), so a given
 * hospital reads with one consistent color everywhere, regardless of which subset
 * a particular view happens to show or how it's sorted on screen.
 *
 * Before this, each view assigned palette colors by its own local order — the map
 * by the hospitals present in the current scans, the bar chart by bar position —
 * so the same hospital could be red in one place and gold in another.
 */

// Official G-Data Labs brand palette — 18 colors, enough for 15+ sites.
// ORDER MATTERS: hospitals are assigned colors by index, so the order below is
// the priority order, taken straight from the brand logo:
//   1) the TEN colors that appear in the night logo (sampled from it) — the two
//      primaries red & blue, then plum, burnt orange, lavender, sage green, gold,
//      bright amber, orange, and the brain's light gray;
//   2) the secondary red (crimson) and secondary blue (teal);
//   3) the remaining warm shades ordered lightest → darkest, ending on dark gray.
// This keeps a dozen sites reading as a dozen on-brand, clearly-different colors
// instead of a wash of similar golds.
export const HOSPITAL_PALETTE = [
  // ── the ten logo colors ──
  "#EE3E2E", // brand red      (logo — brain left / top ray)
  "#3B7BBE", // brand blue     (logo — brain right / bottom ray)
  "#7C3A73", // plum purple    (logo ray)
  "#EA6D24", // burnt orange   (logo ray)
  "#907CB4", // lavender       (logo ray)
  "#A1BF8C", // sage green     (logo ray)
  "#DDAF41", // gold           (logo — the "G")
  "#E4E4E3", // light gray     (logo — the brain)
  "#FAB515", // bright amber   (logo ray)
  "#F5B11A", // orange         (logo ray)
  // ── secondary red, secondary blue ──
  "#E62431", // crimson
  "#046C98", // teal
  // ── remaining shades, lightest → darkest ──
  "#EFDCB1", // cream
  "#E9CB84", // pale gold
  "#E4C375", // light gold
  "#E2BB5E", // amber tan
  "#E1BA5C", // tan/beige
  "#333333", // dark gray (darkest)
];

const norm = (s: string) => s.trim().toLowerCase();

// Per-hospital pinned colors, keyed by normalized hospital name. These take
// precedence over the automatic alphabetical assignment, and the auto-assigned
// hospitals skip any pinned color so nothing ends up sharing. Add a line here to
// lock a specific hospital to a specific brand color.
export const HOSPITAL_COLOR_OVERRIDES: Record<string, string> = {
  "emory university hospital (euh)": "#333333", // dark gray
  "northlake": "#DDAF41", // gold
  "emory saint joseph hospital": "#A1BF8C", // sage green
  "winship at emory midtown": "#E4E4E3", // light gray
};

// Stable string hash → palette index. Only used as a fallback for a hospital
// name/id that isn't in the canonical list (e.g. "Unknown Hospital"), so it still
// gets a deterministic, repeatable color instead of shifting around.
function hashIndex(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % HOSPITAL_PALETTE.length;
}

export interface HospitalColorMap {
  /** hospital id -> color */
  byId: Map<string, string>;
  /** normalized hospital name -> color */
  byName: Map<string, string>;
  /** Resolve a color by id first, then name, then a stable hash fallback. */
  colorFor: (opts: { id?: string | null; name?: string | null }) => string;
}

/**
 * Build the canonical hospital→color map from a list of hospitals. Sorting by name
 * (then id) gives a stable ordering that's identical in every view fed the same
 * full hospital list, so palette slot N always belongs to the same hospital.
 */
export function buildHospitalColorMap(
  hospitals: Array<{ id?: string | null; name?: string | null }>
): HospitalColorMap {
  const sorted = [...(hospitals || [])]
    .filter((h) => h && (h.name || h.id))
    .sort((a, b) =>
      (a.name || a.id || "").toString().localeCompare((b.name || b.id || "").toString())
    );

  const byId = new Map<string, string>();
  const byName = new Map<string, string>();

  // Colors already claimed by a pinned override — auto-assignment skips these so
  // a pinned hospital never shares its color with an alphabetically-placed one.
  const claimed = new Set<string>();
  for (const h of sorted) {
    const ov = h.name ? HOSPITAL_COLOR_OVERRIDES[norm(h.name)] : undefined;
    if (ov) claimed.add(ov);
  }

  // Walk the palette in order, handing out the next color that isn't pinned.
  let palIdx = 0;
  const nextColor = () => {
    while (palIdx < HOSPITAL_PALETTE.length && claimed.has(HOSPITAL_PALETTE[palIdx])) palIdx++;
    const color = HOSPITAL_PALETTE[palIdx % HOSPITAL_PALETTE.length];
    palIdx++;
    return color;
  };

  for (const h of sorted) {
    const ov = h.name ? HOSPITAL_COLOR_OVERRIDES[norm(h.name)] : undefined;
    const color = ov || nextColor();
    if (h.id) byId.set(h.id, color);
    if (h.name) byName.set(norm(h.name), color);
  }

  const colorFor = ({ id, name }: { id?: string | null; name?: string | null }) => {
    if (id && byId.has(id)) return byId.get(id)!;
    if (name && byName.has(norm(name))) return byName.get(norm(name))!;
    if (name && HOSPITAL_COLOR_OVERRIDES[norm(name)]) return HOSPITAL_COLOR_OVERRIDES[norm(name)];
    return HOSPITAL_PALETTE[hashIndex(norm(name || id || "unknown"))];
  };

  return { byId, byName, colorFor };
}

/**
 * React hook: fetches the full hospital list once and returns the shared color
 * map. Use this in any view that colors by hospital but doesn't already hold the
 * full hospital list (e.g. the comparison chart, which only has scan rows).
 */
export function useHospitalColors(): HospitalColorMap {
  const [hospitals, setHospitals] = useState<Array<{ id?: string | null; name?: string | null }>>([]);
  useEffect(() => {
    let alive = true;
    fetchHospitals()
      .then((hs) => { if (alive) setHospitals(hs); })
      .catch(() => { if (alive) setHospitals([]); });
    return () => { alive = false; };
  }, []);
  return useMemo(() => buildHospitalColorMap(hospitals), [hospitals]);
}
